/**
 * @version 1.3
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://kelnik.gitbooks.io/kelnik-documentation/content/front-end/components/utils.html documentation
 */

/**
 * DEPENDENCIES
 */
import 'url-search-params-polyfill';
import {clearAllBodyScrollLocks, disableBodyScroll} from 'body-scroll-lock';
import {DESKTOP_BREAKPOINT} from './constants';

class Utils {
    static isTouchDevice() {
        return (
            Boolean(typeof window !== 'undefined' &&
                ('ontouchstart' in window ||
                    (window.DocumentTouch &&
                        typeof document !== 'undefined' &&
                        document instanceof window.DocumentTouch))) ||
            Boolean(typeof navigator !== 'undefined' &&
                (navigator.maxTouchPoints || navigator.msMaxTouchPoints))
        );
    }

    /**
     * Метод полностью очищает весь html элемент.
     * @param {Object} element - DOM-элемент, который необходимо очистить.
     */
    static clearHtml(element) {
        element.innerHTML = '';
    }

    /**
     * Метод вставляет содержимое в блок.
     * @param {Object} element - элемент в который нужно вставить.
     * @param {Object/string} content - вставляемый контент.
     */
    static insetContent(element, content) {
        if (typeof content === 'string') {
            element.insertAdjacentHTML('beforeend', content);
        } else if (typeof content === 'object') {
            element.appendChild(content);
        }
    }

    /**
     * Метод полностью удаляет элемент из DOM-дерева.
     * @param {HTMLElement} element - элемент, который необходимо удалить.
     */
    static removeElement(element) {
        // node.remove() не работает в IE11
        element.parentNode.removeChild(element);
    }


    /**
     * Метод показывает элемент.
     * @param {Node} element - элемент, который необходимо показать.
     */
    static show(element) {
        element.style.display = 'block';
    }

    /**
     * Метод скрывает элемент.
     * @param {Node} element - элемент, который необходимо скрыть.
     */
    static hide(element) {
        element.style.display = 'none';
    }

    /**
     * Узнает index элемента в родительской элемент
     * Аналог jquery.index()
     * @param {Node} element - искомый элемент
     * @return {number} - порядковый номер (индекс) в родительском элементе
     */
    static getElementIndex(element) {
        return Array.from(element.parentNode.children).indexOf(element);
    }

    /**
     * Узнает находится ли элемент во вьюпорте
     * @param {Node} element - искомый элемент
     * @param {Number} offset - дополнительный отступ
     * @return {boolean} - возвращает true, если элемент виден на экране, и false наоборот
     */
    static isInViewport(element, offset = 0) {
        const rect = element.getBoundingClientRect();
        const top = rect.top + offset;
        const left = rect.left + offset;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;
        const belowViewport = 0;

        const verticalInView = (top <= windowHeight) && ((top + rect.height) >= belowViewport);
        const horizontalInView = (left <= windowWidth) && ((left + rect.width) >= belowViewport);

        return verticalInView && horizontalInView;
    }

    /**
     * Определяет ширину экрана
     * @returns {Number} Значение ширины экрана
     */
    static getWindowWidth() {
        return window.innerWidth || document.documentElement.clientWidth;
    }

    /**
     * Определяет ширину экрана
     * @returns {Number} Значение ширины экрана
     */
    static getScreenWidth() {
        return window.screen.width;
    }

    /**
     * Определяет высоту экрана
     * @returns {Number} Значение ширины экрана
     */
    static getScreenHeight() {
        return window.screen.height;
    }

    /**
     * Определяет высоту экрана
     * @returns {Number} Значение высоты экрана
     */
    static getWindowHeight() {
        return window.innerHeight || document.documentElement.clientHeight;
    }

    /**
     * Проверяет, мобилка или нет
     * @returns {Boolean} - true - мобилка
     */
    static isMobile() {
        return Utils.getWindowWidth() < Number(DESKTOP_BREAKPOINT);
    }

    /**
     * Проверяет, мобилка/планшет или нет через данные браузера
     * Скрипт взят с http://detectmobilebrowsers.com/
     * @returns {Boolean} - true - мобилка
     */
    static isMobileOrTabletJs() {
        let check = false;

        // eslint-disable-next-line func-names
        (function(a) {
            // eslint-disable-next-line
            if ((/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i).test(a) || (/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i).test(a.substr(0, 4))) {
                check = true;
            }
        })(navigator.userAgent || navigator.vendor || window.opera);

        return check;
    }

    /**
     * Фиксирует страницу для запрета прокрутки
     * @param {Node} element - кроме данного элемента у всех остальных сбросится скролл
     */
    /* eslint-disable  */
    static bodyFixed(element) {
        disableBodyScroll(element, {
            allowTouchMove: (el) => {
                while (el && el !== document.body) {
                    if (el.getAttribute('body-scroll-lock-ignore') !== null) {
                        return true;
                    }

                    el = el.parentNode;
                }
            },
            reserveScrollBarGap: true
        });

        const padding = getComputedStyle(document.body).paddingRight;
        const header = document.querySelector('.header');

        if (header) {
            header.style.paddingRight = padding;
        }
    }
    /* eslint-enable  */

    /**
     * Снимаем фиксирование страницы
     */
    static bodyStatic() {
        clearAllBodyScrollLocks();

        const header = document.querySelector('.header');

        if (header) {
            header.style.paddingRight = '0';
        }
    }


    /**
     * Устанавливает гет-параметры
     * @param {string} url - адрес скрипта/API который будем подключать
     * @param {object} callback - функция обратного вызова, если нужна
     */
    static loadScript(url, callback) {
        const script = document.createElement('script');

        // eslint-disable-next-line consistent-return
        script.onload = () => {
            if (callback) {
                return callback();
            }
        };

        script.src = url;
        document.getElementsByTagName('head')[0].appendChild(script);
    }

    static loadHandler(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();

            img.src = url;
            img.onload = (event) => {
                const src = event.currentTarget.getAttribute('src');
                const isSvg = src.indexOf('.svg');

                if (isSvg === -1) {
                    resolve({
                        width : event.currentTarget.width,
                        height: event.currentTarget.height
                    });
                } else {
                    $.get(src, (svgxml) => {
                        const viewBox = svgxml.documentElement.attributes.viewBox.value.split(' ');

                        resolve({
                            width : Number(viewBox[2]),
                            height: Number(viewBox[3])
                        });
                    }, 'xml');
                }
            };
            img.onerror = (error) => {
                reject(error);
            };
        });
    }

    /**
     * Расставляет мягкий перенос в строке
     * @param {String} string - текст, в котором надо расставить переносы
     * @return {String} - текст с переносами
     */
    static hyphenate(string) {
        let text = string;
        const all = '[а-яА-Я]';
        const glas = '[аеёиоуыэюяАЕЁИОУЫЭЮЯ]';
        const sogl = '[бвгджзклмнпрстфхцчшщБВГДЖЗКЛМНПРСТФХЦШЩ]';
        const zn = '[йъьЙЪЬ]';
        const shy = '\xAD';
        const re = [];

        re[1] = new RegExp(`(${zn})(${all}${all})`, 'ig');
        re[2] = new RegExp(`(${glas})(${glas}${all})`, 'ig');
        re[4] = new RegExp(`(${glas}${sogl})(${sogl}${glas})`, 'ig');
        re[3] = new RegExp(`(${sogl}${glas})(${sogl}${glas})`, 'ig');
        re[5] = new RegExp(`(${glas}${sogl})(${sogl}${sogl}${glas})`, 'ig');
        re[6] = new RegExp(`(${glas}${sogl}${sogl})(${sogl}${sogl}${glas})`, 'ig');

        for (let j = 0; j < 2; ++j) {
            for (let i = 0; i <= 6; ++i) {
                text = text.replace(re[i], `$1${shy}$2`);
            }
        }

        return text;
    }


    /**
     * Debouncing функции означает, что все вызовы будут игнорироваться до тех пор,
     * пока они не прекратятся на определённый период времени. Только после этого функция будет вызвана.
     * @param {function} fun - Функция выполнения
     * @param {number} time - Миллисекунды
     * @return {function} - Замыкание
     */
    static debounce(fun, time) {
        let lastCall = null;

        return (args) => {
            const previousCall = lastCall;

            lastCall = Date.now();

            if (previousCall && ((lastCall - previousCall) <= time)) {
                clearTimeout(this.lastCallTimer);
            }

            this.lastCallTimer = setTimeout(() => {
                fun(args);
            }, time);
        };
    }

    /**
     * Метод проверяет наличие интернета
     * @return {boolean} - При наличии результатом будет true, а при отсутствии false.
     */
    static checkInternetConnection() {
        return navigator.onLine;
    }

    /**
     * Метод отправляет ajax запрос на сервер.
     * @param {Object} data - отправляемые данные.
     * @param {String} url - маршрут по которому нужно произвести запрос.
     * @param {Object} callback -  функции обратного вызова, при успехе вызовет поле success, а при ошибке - error.
     * @param {String} method - метод отправки запроса
     */
    static send(data, url, callback = {}, method = 'GET') {
        const xhr = new XMLHttpRequest();
        const statusSuccess = 200;

        xhr.open(method, url);

        if (!(data instanceof FormData)) {
            xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
        }

        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        xhr.send(data);

        xhr.onload = function XHR() {
            if (xhr.status === statusSuccess) {
                const req = JSON.parse(this.responseText);

                if (callback.success) {
                    callback.success(req);
                }
            } else if (callback.error) {
                callback.error(xhr.status);
            }

            if (callback.complete) {
                callback.complete();
            }
        };

        xhr.onerror = function XHR() {
            if (callback.error) {
                callback.error(xhr.status);
            }

            if (callback.complete) {
                callback.complete();
            }
        };
    }

    /**
     * Генерация псевдослучайных чисел в заданном числовом интервале
     * @param {Number} min - минимальное псевдослучайное число
     * @param {Number} max - максимальное псевдослучайное число
     * @return {Number} - возвращает псевдослучайное число в интервале [min, max]
     */
    static random(min, max) {
        return Math.floor((Math.random() * (max - min)) + min);
    }

    /**
     * Конвертирует число или строку в строку с рарядами.
     * @param {string/number} value - число или строка.
     * @return {string} - преобразованная строка.
     */
    static convertToDigit(value) {
        return Number(value) ? Number(value).toLocaleString('ru-Ru') : value;
    }

    /**
     * Конвертирует целое число в дробное
     * @param {string/number} value - число или строка.
     * @param {number} denominator - целый делитель.
     * @return {number} - преобразованная строка.
     */
    static convertToRank(value, denominator) {
        const result = Number(value);

        return result / denominator;
    }

    /**
     * Метод конвертирует строку (можно с разрядами) в число
     * @param {string} str - необходимая строка.
     * @return {number} - сковертированное число.
     */
    static convertToNumber(str) {
        return parseFloat(str
            .toString()
            .replace(/\s/g, ''));
    }

    /**
     * Выбирает окончание слова.
     * @param {number} n - количество
     * @param {Object} words - масcив слов. Например показать еще ['квартиру', 'квартиры', 'квартир']
     * @return {string} - слово в правильном склонении.
     */
    static pluralWord(n, words) {
        /* eslint-disable  */
        const $i = n % 10 === 1 && n % 100 !== 11 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2;
        /* eslint-enable*/

        return words[$i];
    }

    /**
     * Получает значение гет-параметра
     * @param {string} param - ключ для поиска в гет
     * @return {string} value - значение гет
     */
    static getUrlParams(param) {
        const get = window.location.search;
        const url = new URLSearchParams(get);

        return url.get(param);
    }

    /**
     * Проверяет, является ли сущность дом элементом
     * @param {*} elem - элемент для проверки
     * @returns {boolean} true - является
     */
    static isElement(elem) {
        return Boolean(elem.tagName);
    }

    static closest(element, parent) {
        if (!element || !parent) {
            return false;
        }

        return element.closest(parent);
    }

    /**
     * Проверяет, является ли parent родителем child
     * @param {HTMLElement} child - наследник
     * @param {HTMLElement} parent - родитель
     * @return {boolean} - true - является
     */
    static isDescendant(child, parent) {
        let node = child.parentNode;

        while (node !== null) {
            if (node === parent) {
                return true;
            }
            node = node.parentNode;
        }

        return false;
    }

    /**
     * Возвращает пустая ли переменная
     * @param {any} variable - переменная для проверки
     * @return {boolean} - пустая ли переменная?
     */
    static isSet(variable) {
        return !(typeof variable === 'undefined' || variable === null);
    }

    /**
     * Получает все гет-параметры из url-а
     * @return {object} - объект параметров
     */
    static getAllUrlParams() {
        const urlSearch = decodeURIComponent(window.location.search);
        const getParams = urlSearch.match(new RegExp('[^&?]+', 'gm'));
        const result = {};

        if (getParams) {
            getParams.forEach((param) => {
                const splitParam = param.split('=');

                if (splitParam[0] && splitParam[1]) {
                    result[splitParam[0]] = splitParam[1];
                }
            });
        }

        return result;
    }

    /**
     * Метод проверяет присутствует ли ключ в объекте
     * @param {Object} object - проверяем объект
     * @param {String} key - ключ, наличие которого проверяет в объекте
     * @return {boolean} - присутствует или нет ключ в объекте
     */
    static keyExist(object, key) {
        return Object.prototype.hasOwnProperty.call(object, key);
    }

    /**
     * Установка cookie
     * @param {string} name - имя cookie
     * @param {any} value - значение cookie
     * @param {object} options - объект опций
     */
    static setCookie(name, value, options = {}) {
        // eslint-disable-next-line no-param-reassign
        options = {
            path: '/',
            ...options
        };

        if (options.expires instanceof Date) {
            options.expires = options.expires.toUTCString();
        }

        let updatedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

        for (const optionKey in options) {
            if (Utils.keyExist(options, optionKey)) {
                updatedCookie = `${updatedCookie}; ${optionKey}`;

                const optionValue = options[optionKey];

                if (optionValue !== true) {
                    updatedCookie = `${updatedCookie}=${optionValue}`;
                }
            }
        }
        document.cookie = updatedCookie;
    }

    /**
     * Получает cookie по имени
     * @param {string} name - имя куки
     * @return {*} - данные из куки
     */
    static getCookie(name) {
        const matches = document.cookie
            .match(new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`));

        return matches ?
            decodeURIComponent(matches[1]) :
            // eslint-disable-next-line no-undefined
            undefined;
    }

    /**
     * Удаляет cookie по имени
     * @param {string} name - имя куки
     */
    static deleteCookie(name) {
        Utils.setCookie(name, '', {
            'max-age': -1
        });
    }

    /**
     * Определяет айфон или нет
     * @returns {boolean} - возвращает устройство айфон или нет
     */
    static isSafari() {
        const userAgent = window.navigator?.userAgent.toLowerCase();

        return Boolean(userAgent.match(/iphone/i) || userAgent.match(/ipad/i) ||
            (!userAgent.match(/chrome/i) && userAgent.match(/safari/i)));
    }
}

class Counter {
    constructor(stopValue = 3) {
        this.stopValue = stopValue;
        this.countValue = this.stopValue;
    }

    tick() {
        if (!this.countValue) {
            this.countValue = this.stopValue;

            return true;
        }

        this.countValue--;

        return false;
    }
}

class InternetConnection {
    constructor(callback = () => {
        return null;
    }, interval = 200) {
        this.counter = new Counter(3);

        this.callback = callback;
        this.interval = interval;

        this._bindMethodsContext();
    }

    connect() {
        if (!Utils.checkInternetConnection() && !this.counter.tick()) {
            this._reconnect(this.connect);

            return;
        }

        this.callback();
    }

    _bindMethodsContext() {
        this.connect = this.connect.bind(this);
    }

    /**
     * @Public
     * Возвращает контент с ошибками сети
     * @return {Object} - объект со списком контента для обработки ошибок
     */
    getErrorMessage() {
        return Utils.checkInternetConnection() ?
            '<h1>На сервере произошла ошибка. Повторите запрос позднее</h1>' :
            '<h1>Вы не подключены к интернету. Повторите запрос позднее</h1>';
    }

    _reconnect(callback) {
        this.setInterval = setTimeout(() => {
            callback();
        }, this.interval);
    }
}

export {Utils, InternetConnection};
